import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FiUsers,
  FiCalendar,
  FiFilter,
  FiRefreshCw,
  FiUserCheck,
  FiChevronDown,
  FiChevronUp,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiFileText,
  FiAlertCircle,
  FiCheckCircle,
  FiDownload,
  FiDollarSign,
} from 'react-icons/fi';
import { adminApi, dashboardApi } from '../../api';
import { Loader } from '../Common/Loader';
import { showToast } from '../../utils/toast';
import { useAuthStore } from '../../stores';

type ReportMode = 'admin' | 'master';

interface AdminMasterFollowingReportProps {
  /** admin = full audit UI; master = own report only, default last 15 days */
  mode?: ReportMode;
}

interface UserRef {
  _id?: string;
  name?: string;
  email?: string;
  role?: string;
}

interface FollowingReportRecord {
  _id: string;
  masterId: UserRef | null;
  usersId: UserRef[];
  createdAt: string;
  updatedAt: string;
}

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

const MASTER_WEEKDAY_REPORT_DAYS = 15;

// Helper to check if a given date is a non-working day (Saturday or Sunday)
const isNonWorkingDay = (dateInput: string | Date): boolean => {
  const d = new Date(dateInput);
  const day = d.getDay(); // 0 = Sunday, 6 = Saturday
  return day === 0 || day === 6;
};

/** Calendar start date covering the last N weekdays (ending today). */
const getStartDateForWeekdays = (weekdayCount: number): string => {
  const d = new Date();
  let remaining = Math.max(1, weekdayCount);
  while (remaining > 0) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) {
      remaining -= 1;
      if (remaining === 0) break;
    }
    d.setDate(d.getDate() - 1);
  }
  return d.toISOString().split('T')[0];
};

/** Wider fetch window so missing/extra weekend snapshots still yield N weekdays. */
const getMasterFetchStartDate = (weekdayCount: number): string => {
  const d = new Date();
  // ~ weekdayCount weekdays + weekends buffer
  d.setDate(d.getDate() - (weekdayCount + 14));
  return d.toISOString().split('T')[0];
};

const toDateKey = (value: string | Date): string => {
  const d = new Date(value);
  return d.toISOString().split('T')[0];
};

export const AdminMasterFollowingReport: React.FC<AdminMasterFollowingReportProps> = ({
  mode = 'admin',
}) => {
  const isMasterMode = mode === 'master';
  const { user } = useAuthStore();
  const [masters, setMasters] = useState<UserRef[]>([]);
  const [selectedMasterId, setSelectedMasterId] = useState<string>(() =>
    isMasterMode && user?._id ? user._id : 'all'
  );
  const [startDate, setStartDate] = useState<string>(() =>
    isMasterMode
      ? getStartDateForWeekdays(MASTER_WEEKDAY_REPORT_DAYS)
      : (() => {
          const d = new Date();
          d.setDate(d.getDate() - 30);
          return d.toISOString().split('T')[0];
        })()
  );
  const [endDate, setEndDate] = useState<string>(() => new Date().toISOString().split('T')[0]);

  // Non-Working Days switch & Daily Fee rate states
  // Master mode: always exclude weekends; rate comes from single per-user input (default $0.50)
  const [excludeNonWorkingDays, setExcludeNonWorkingDays] = useState<boolean>(isMasterMode);
  const [dailyFeeRate, setDailyFeeRate] = useState<number | string>(isMasterMode ? 0.5 : 1);

  const effectiveExcludeWeekends = isMasterMode ? true : excludeNonWorkingDays;
  const effectiveDailyFeeRate = dailyFeeRate;

  // Pagination states (master shows up to 15 weekday rows on one page)
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(isMasterMode ? MASTER_WEEKDAY_REPORT_DAYS : 10);
  const [pagination, setPagination] = useState<PaginationMeta>({
    total: 0,
    page: 1,
    limit: 10,
    pages: 1,
  });

  const [reports, setReports] = useState<FollowingReportRecord[]>([]);
  const [allFilteredReports, setAllFilteredReports] = useState<FollowingReportRecord[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isTriggering, setIsTriggering] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [expandedReports, setExpandedReports] = useState<Set<string>>(new Set());

  // Keep master mode locked to the logged-in master
  useEffect(() => {
    if (isMasterMode && user?._id) {
      setSelectedMasterId(user._id);
    }
  }, [isMasterMode, user?._id]);

  // Load active master traders for filter dropdown (admin only)
  useEffect(() => {
    if (isMasterMode) return;
    const fetchMasters = async () => {
      try {
        const res = await adminApi.getMasters();
        if (res.success && res.users) {
          setMasters(res.users as any[]);
        }
      } catch (err) {
        console.error('Failed to load master traders:', err);
      }
    };
    fetchMasters();
  }, [isMasterMode]);

  const fetchReportsApi = useCallback(
    (params: {
      masterId?: string;
      startDate?: string;
      endDate?: string;
      page?: number;
      limit?: number;
    }) => {
      if (isMasterMode) {
        const { masterId: _ignored, ...masterParams } = params;
        return dashboardApi.getMyMasterFollowingReports(masterParams);
      }
      return adminApi.getMasterFollowingReports(params);
    },
    [isMasterMode]
  );

  // Fetch reports with pagination & date filters + full range for global fee calculation
  const handleFetchReports = useCallback(async (targetPage = page, targetLimit = limit) => {
    try {
      setIsLoading(true);

      // Master: last 15 weekdays only (weekends never counted or listed)
      if (isMasterMode) {
        const end = new Date().toISOString().split('T')[0];
        const weekdayStart = getStartDateForWeekdays(MASTER_WEEKDAY_REPORT_DAYS);
        const fetchStart = getMasterFetchStartDate(MASTER_WEEKDAY_REPORT_DAYS);
        setStartDate(weekdayStart);
        setEndDate(end);

        const fullRes = await fetchReportsApi({
          startDate: fetchStart,
          endDate: end,
          limit: 5000,
        });

        const weekdayReports = (fullRes.reports || [])
          .filter((report) => !isNonWorkingDay(report.createdAt))
          // one snapshot per calendar day (newest first already from API)
          .filter((report, index, arr) => {
            const key = toDateKey(report.createdAt);
            return arr.findIndex((r) => toDateKey(r.createdAt) === key) === index;
          })
          .slice(0, MASTER_WEEKDAY_REPORT_DAYS);

        setReports(weekdayReports);
        setAllFilteredReports(weekdayReports);
        setPagination({
          total: weekdayReports.length,
          page: 1,
          limit: MASTER_WEEKDAY_REPORT_DAYS,
          pages: 1,
        });
        setExpandedReports(new Set());
        return;
      }

      const [res, fullRes] = await Promise.all([
        fetchReportsApi({
          masterId: selectedMasterId !== 'all' ? selectedMasterId : undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          page: targetPage,
          limit: targetLimit,
        }),
        fetchReportsApi({
          masterId: selectedMasterId !== 'all' ? selectedMasterId : undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          limit: 5000,
        }),
      ]);

      if (res.success) {
        const reportItems = res.reports || [];
        setReports(reportItems);

        if (res.pagination) {
          setPagination(res.pagination);
        } else {
          setPagination({
            total: reportItems.length,
            page: targetPage,
            limit: targetLimit,
            pages: Math.ceil(reportItems.length / targetLimit) || 1,
          });
        }

        // Keep report cards collapsed by default
        setExpandedReports(new Set());
      }

      if (fullRes.success) {
        setAllFilteredReports(fullRes.reports || []);
      }
    } catch (err: any) {
      showToast('error', err?.response?.data?.error || 'Failed to fetch master following reports');
    } finally {
      setIsLoading(false);
    }
  }, [isMasterMode, selectedMasterId, startDate, endDate, page, limit, fetchReportsApi]);

  // Initial load and refetch on page or limit change
  useEffect(() => {
    handleFetchReports(page, limit);
  }, [page, limit, handleFetchReports]);

  // Fee calculation memo across all matching report entries for selected range & master
  const feeCalculation = useMemo(() => {
    const rate = Math.max(0, Number(effectiveDailyFeeRate) || 0);
    let includedDaysCount = 0;
    let excludedDaysCount = 0;
    let totalFollowerDays = 0;

    allFilteredReports.forEach((report) => {
      const isWeekend = isNonWorkingDay(report.createdAt);
      if (effectiveExcludeWeekends && isWeekend) {
        excludedDaysCount += 1;
      } else {
        includedDaysCount += 1;
        const followerCount = (report.usersId || []).length;
        totalFollowerDays += followerCount;
      }
    });

    const totalFee = totalFollowerDays * rate;

    return {
      rate,
      includedDaysCount,
      excludedDaysCount,
      totalFollowerDays,
      totalFee,
    };
  }, [allFilteredReports, effectiveExcludeWeekends, effectiveDailyFeeRate]);

  // Master mode reports are already weekday-only (max 15)
  const visibleReports = reports;

  // Handle Master change
  const handleMasterChange = (masterId: string) => {
    setSelectedMasterId(masterId);
    setPage(1);
  };

  // Handle Apply button click
  const handleApplyFilters = () => {
    setPage(1);
    handleFetchReports(1, limit);
  };

  // Trigger snapshot generation manually (admin only)
  const handleTriggerSnapshot = async () => {
    if (isMasterMode) return;
    try {
      setIsTriggering(true);
      const res = await adminApi.triggerMasterFollowingReport();
      if (res.success) {
        showToast('success', res.message || 'Daily master following report snapshot captured!');
        setPage(1);
        handleFetchReports(1, limit);
      }
    } catch (err: any) {
      showToast('error', err?.response?.data?.error || 'Failed to capture snapshot');
    } finally {
      setIsTriggering(false);
    }
  };

  // CSV Download Handler
  const handleDownloadCSV = async () => {
    try {
      setIsDownloading(true);
      const res = await fetchReportsApi({
        masterId: selectedMasterId !== 'all' ? selectedMasterId : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        limit: 5000,
      });

      const exportReports = (res.reports || []).filter((report) =>
        isMasterMode ? !isNonWorkingDay(report.createdAt) : true
      );
      if (exportReports.length === 0) {
        showToast('error', 'No report data available to export for the selected date range.');
        return;
      }

      const headers = [
        'Report ID',
        'Date',
        'Time',
        'Master Name',
        'Master Email',
        'Total Followers On Date',
        'Follower Name',
        'Follower Email',
        'Follower Role',
        'Is Non-Working Day (Weekend)',
        'Included In Fee Calculation',
        'Daily Rate ($)',
        'Daily Master Fee ($)',
      ];

      const rows: string[][] = [headers];

      const rateNum = Math.max(0, Number(effectiveDailyFeeRate) || 0);

      exportReports.forEach((report) => {
        const dateObj = new Date(report.createdAt);
        const dateStr = dateObj.toLocaleDateString();
        const timeStr = dateObj.toLocaleTimeString();
        const masterName = report.masterId?.name || 'Unknown Master';
        const masterEmail = report.masterId?.email || '';
        const totalFollowers = (report.usersId || []).length;
        const isWeekend = isNonWorkingDay(report.createdAt);
        const isExcluded = effectiveExcludeWeekends && isWeekend;
        const dailyFee = isExcluded ? 0 : totalFollowers * rateNum;

        if (!report.usersId || report.usersId.length === 0) {
          rows.push([
            report._id,
            dateStr,
            timeStr,
            masterName,
            masterEmail,
            '0',
            'No Followers',
            'N/A',
            'N/A',
            isWeekend ? 'Yes (Weekend)' : 'No (Weekday)',
            isExcluded ? 'Excluded' : 'Included',
            `$${rateNum.toFixed(2)}`,
            `$${dailyFee.toFixed(2)}`,
          ]);
        } else {
          report.usersId.forEach((follower) => {
            rows.push([
              report._id,
              dateStr,
              timeStr,
              masterName,
              masterEmail,
              totalFollowers.toString(),
              follower?.name || 'N/A',
              follower?.email || 'N/A',
              follower?.role || 'follower',
              isWeekend ? 'Yes (Weekend)' : 'No (Weekday)',
              isExcluded ? 'Excluded' : 'Included',
              `$${rateNum.toFixed(2)}`,
              `$${dailyFee.toFixed(2)}`,
            ]);
          });
        }
      });

      const csvContent = rows
        .map((row) =>
          row
            .map((field) => {
              const escaped = String(field || '').replace(/"/g, '""');
              return `"${escaped}"`;
            })
            .join(',')
        )
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Master_Following_Report_${startDate}_to_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showToast('success', 'Master Following Report exported to CSV successfully!');
    } catch (err: any) {
      showToast('error', err?.response?.data?.error || 'Failed to export CSV');
    } finally {
      setIsDownloading(false);
    }
  };

  const toggleExpand = (reportId: string) => {
    setExpandedReports((prev) => {
      const next = new Set(prev);
      if (next.has(reportId)) next.delete(reportId);
      else next.add(reportId);
      return next;
    });
  };

  // Date range presets
  const handlePresetDate = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
    setPage(1);
  };

  const isSingleDaySelected = startDate === endDate;
  const selectedRangeDays = useMemo(() => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  }, [startDate, endDate]);

  // Analysis of follower repetitions if single day or date range selected
  const userRepeatAnalysis = useMemo(() => {
    const counts: Record<string, { user: UserRef; count: number; dates: string[] }> = {};

    visibleReports.forEach((report) => {
      const dateStr = new Date(report.createdAt).toLocaleDateString();
      (report.usersId || []).forEach((u) => {
        if (!u || !u._id) return;
        if (!counts[u._id]) {
          counts[u._id] = { user: u, count: 0, dates: [] };
        }
        counts[u._id].count += 1;
        counts[u._id].dates.push(dateStr);
      });
    });

    return counts;
  }, [visibleReports]);

  // Selected master trader details object
  const activeMasterObj = useMemo(() => {
    if (selectedMasterId === 'all') return null;
    return masters.find((m) => m._id === selectedMasterId) || null;
  }, [masters, selectedMasterId]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white rounded-2xl p-4 sm:p-8 shadow-xl border border-white/10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 text-xs font-semibold uppercase tracking-wider mb-3">
              <FiFileText className="h-3.5 w-3.5" />
              {isMasterMode ? 'Your Following Report' : 'Admin Master Audit'}
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
              Master Following Report
            </h1>
            <p className="mt-1 text-slate-300 text-sm max-w-2xl">
              {isMasterMode
                ? 'Last 15 weekdays of followers. Set your rate per user — earnings update automatically (weekends excluded).'
                : 'Audit historical follower snapshots stored in DB date-wise per master trader with full pagination. View follower counts, names, emails, and repeat occurrences.'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 self-start md:self-auto">
            <button
              type="button"
              onClick={handleDownloadCSV}
              disabled={isDownloading}
              className="px-5 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl shadow-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50 whitespace-nowrap"
            >
              <FiDownload className={`h-4 w-4 ${isDownloading ? 'animate-bounce' : ''}`} />
              {isDownloading ? 'Exporting CSV...' : 'Download CSV'}
            </button>

            {!isMasterMode && (
              <button
                type="button"
                onClick={handleTriggerSnapshot}
                disabled={isTriggering}
                className="px-5 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-xl shadow-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50 whitespace-nowrap"
              >
                <FiRefreshCw className={`h-4 w-4 ${isTriggering ? 'animate-spin' : ''}`} />
                {isTriggering ? 'Capturing Snapshot...' : 'Generate Today\'s Snapshot'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Master: only per-user rate input. Admin: full filters. */}
      {isMasterMode ? (
        <div className="bg-surface border border-border rounded-2xl p-4 sm:p-6 shadow-sm">
          <label className="block text-xs font-semibold text-muted mb-2 uppercase tracking-wider">
            Per user / day ($)
          </label>
          <div className="relative max-w-xs">
            <FiDollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-muted h-4 w-4 pointer-events-none z-10" />
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="0.50"
              value={dailyFeeRate}
              onChange={(e) => setDailyFeeRate(e.target.value)}
              className="w-full pl-9 pr-3 py-3 bg-background border border-border rounded-xl text-base font-bold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
        </div>
      ) : (
      <div className="bg-surface border border-border rounded-2xl p-4 sm:p-6 shadow-sm space-y-4">
        <h2 className="text-base font-bold text-foreground flex items-center gap-2">
          <FiFilter className="h-4 w-4 text-primary" />
          Filter Report Data & Fee Parameters
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
          <div>
            <label className="block text-xs font-semibold text-muted mb-1 uppercase tracking-wider">
              1. Master Trader
            </label>
            <select
              value={selectedMasterId}
              onChange={(e) => handleMasterChange(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-background border border-border rounded-xl text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer"
            >
              <option value="all">All Master Traders</option>
              {masters.map((m) => (
                <option key={m._id} value={m._id}>
                  {m.name || m.email} ({m.email})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted mb-1 uppercase tracking-wider">
              2. Start Date
            </label>
            <div className="relative">
              <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-muted h-4 w-4 pointer-events-none z-10" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                onClick={(e) => {
                  try {
                    if ('showPicker' in e.currentTarget) {
                      e.currentTarget.showPicker();
                    }
                  } catch (err) { }
                }}
                className="w-full pl-9 pr-3 py-2.5 bg-background border border-border rounded-xl text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted mb-1 uppercase tracking-wider">
              3. End Date
            </label>
            <div className="relative">
              <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-muted h-4 w-4 pointer-events-none z-10" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                onClick={(e) => {
                  try {
                    if ('showPicker' in e.currentTarget) {
                      e.currentTarget.showPicker();
                    }
                  } catch (err) { }
                }}
                className="w-full pl-9 pr-3 py-2.5 bg-background border border-border rounded-xl text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted mb-1 uppercase tracking-wider">
              4. Daily Fee Price ($)
            </label>
            <div className="relative">
              <FiDollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-muted h-4 w-4 pointer-events-none z-10" />
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="1.00"
                value={dailyFeeRate}
                onChange={(e) => setDailyFeeRate(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 bg-background border border-border rounded-xl text-sm font-bold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2 bg-background border border-border px-3 py-2 rounded-xl">
              <span className="text-xs font-semibold text-foreground select-none">
                Exclude Non-Working Days (Sat/Sun)
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={excludeNonWorkingDays}
                onClick={() => setExcludeNonWorkingDays(!excludeNonWorkingDays)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${excludeNonWorkingDays ? 'bg-amber-500' : 'bg-slate-700'
                  }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${excludeNonWorkingDays ? 'translate-x-5' : 'translate-x-0'
                    }`}
                />
              </button>
            </div>

            <button
              type="button"
              onClick={handleApplyFilters}
              disabled={isLoading}
              className="w-full py-2.5 bg-primary hover:bg-primary-hover text-white font-semibold rounded-xl shadow-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            >
              {isLoading ? <Loader fullScreen={false} /> : <FiFilter className="h-4 w-4" />}
              {isLoading ? 'Fetching...' : 'Apply Filters'}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-border/50 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-muted font-medium">Quick Date Presets:</span>
            <button
              type="button"
              onClick={() => handlePresetDate(0)}
              className={`px-3 py-1 rounded-lg border font-semibold transition-all ${isSingleDaySelected
                ? 'bg-primary text-white border-primary shadow-sm'
                : 'bg-background text-muted border-border hover:border-primary/40'
                }`}
            >
              Single Day (Today)
            </button>
            <button
              type="button"
              onClick={() => handlePresetDate(7)}
              className="px-3 py-1 bg-background text-muted border border-border hover:border-primary/40 rounded-lg font-semibold transition-all"
            >
              Last 7 Days
            </button>
            <button
              type="button"
              onClick={() => handlePresetDate(15)}
              className={`px-3 py-1 rounded-lg border font-semibold transition-all ${
                selectedRangeDays === 15
                  ? 'bg-primary text-white border-primary shadow-sm'
                  : 'bg-background text-muted border-border hover:border-primary/40'
              }`}
            >
              Last 15 Days
            </button>
            <button
              type="button"
              onClick={() => handlePresetDate(30)}
              className="px-3 py-1 bg-background text-muted border border-border hover:border-primary/40 rounded-lg font-semibold transition-all"
            >
              Last 30 Days
            </button>
          </div>

          {activeMasterObj && (
            <div className="flex items-center gap-2 min-w-0 max-w-full px-3 py-1 bg-primary/10 border border-primary/20 rounded-lg text-primary font-bold">
              <span className="min-w-0 break-anywhere">Master: {activeMasterObj.name || activeMasterObj.email}</span>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Fee total — masters see money only; admins see full breakdown */}
      {isMasterMode ? (
        <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-r from-emerald-950/70 via-slate-900 to-indigo-950/70 p-4 sm:p-6 shadow-xl">
          <p className="text-sm font-medium text-slate-300 mb-1">Your earnings</p>
          <p className="text-3xl sm:text-4xl break-anywhere font-black tracking-tight text-emerald-400">
            ${feeCalculation.totalFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="mt-2 text-xs text-slate-400">
            {feeCalculation.totalFollowerDays} follower-days × ${Number(effectiveDailyFeeRate || 0).toFixed(2)}
          </p>
        </div>
      ) : (
        <div className="bg-gradient-to-r from-emerald-950/70 via-slate-900 to-indigo-950/70 border border-emerald-500/30 rounded-2xl p-4 sm:p-6 shadow-xl space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-1.5 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-extrabold uppercase tracking-wider border border-emerald-500/40">
                  Master Fee Calculation Summary
                </span>
                {effectiveExcludeWeekends ? (
                  <span className="px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-xs font-bold uppercase tracking-wider border border-amber-500/30">
                    Non-Working Days (Sat/Sun) Excluded
                  </span>
                ) : (
                  <span className="px-2.5 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 text-xs font-bold uppercase tracking-wider border border-indigo-500/30">
                    All Calendar Days Included
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 pt-1">
                <span className="text-slate-300 text-sm font-medium">Total Calculated Fee:</span>
                <span className="text-3xl sm:text-4xl font-black text-emerald-400 tracking-tight">
                  ${feeCalculation.totalFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>

              <p className="text-xs text-slate-300 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>
                  Total Follower-Days Sum: <strong className="text-white font-bold">{feeCalculation.totalFollowerDays}</strong>
                </span>
                <span>•</span>
                <span>
                  Daily Fee Rate: <strong className="text-emerald-300 font-bold">${feeCalculation.rate.toFixed(2)} / follower / day</strong>
                </span>
                <span>•</span>
                <span>
                  Included Days: <strong className="text-emerald-400 font-bold">{feeCalculation.includedDaysCount}</strong>
                </span>
                {effectiveExcludeWeekends && (
                  <>
                    <span>•</span>
                    <span>
                      Excluded Non-Working Days: <strong className="text-amber-400 font-bold">{feeCalculation.excludedDaysCount}</strong>
                    </span>
                  </>
                )}
              </p>
            </div>

            <div className="bg-slate-900/90 border border-emerald-500/30 rounded-xl p-4 text-xs font-mono text-slate-200 self-start md:self-auto w-full md:w-auto md:min-w-[260px] break-anywhere shadow-inner">
              <div className="text-[11px] text-emerald-400 font-bold uppercase tracking-wider font-sans mb-1">
                Calculation Breakdown
              </div>
              <div className="text-slate-300">
                Sum of Followers ({feeCalculation.totalFollowerDays}) × Rate (${feeCalculation.rate.toFixed(2)})
              </div>
              <div className="text-emerald-400 font-extrabold text-sm mt-1 border-t border-white/10 pt-1">
                = ${feeCalculation.totalFee.toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Section */}
      {isLoading ? (
        <div className="py-16 text-center bg-surface border border-border rounded-2xl">
          <Loader message="Fetching Master Following Reports from DB..." />
        </div>
      ) : visibleReports.length === 0 ? (
        <div className="bg-surface border border-border rounded-2xl p-6 sm:p-12 text-center">
          <FiAlertCircle className="h-12 w-12 text-muted mx-auto mb-3" />
          <h3 className="text-lg font-bold text-foreground">No Following Reports Found</h3>
          <p className="text-muted text-sm mt-1 max-w-md mx-auto">
            No report entries match your selected master or date range ({startDate} to {endDate}). Click "Generate Today's Snapshot" to capture a new report entry.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Metrics Overview Bar */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-surface border border-border rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-muted font-medium uppercase tracking-wider">
                  {isMasterMode ? 'Weekday Reports' : 'Total Matching Reports'}
                </p>
                <p className="text-2xl font-black text-foreground mt-0.5">
                  {isMasterMode ? visibleReports.length : pagination.total}
                </p>
                {isMasterMode && (
                  <p className="text-[11px] text-muted mt-0.5">
                    Up to {MASTER_WEEKDAY_REPORT_DAYS} weekdays (weekends excluded)
                  </p>
                )}
              </div>
              <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                <FiFileText className="h-5 w-5" />
              </div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-muted font-medium uppercase tracking-wider">Unique Followers (Page)</p>
                <p className="text-2xl font-black text-emerald-500 mt-0.5">
                  {Object.keys(userRepeatAnalysis).length}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                <FiUsers className="h-5 w-5" />
              </div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-muted font-medium uppercase tracking-wider">
                  {isMasterMode ? 'Range' : 'Date Mode & Page'}
                </p>
                <p className="text-sm font-bold text-foreground mt-1">
                  {isMasterMode ? (
                    <>
                      Last {MASTER_WEEKDAY_REPORT_DAYS} weekdays
                      <span className="block text-xs text-muted font-semibold">No Sat/Sun</span>
                    </>
                  ) : (
                    <>
                      Page {pagination.page} of {pagination.pages}
                      {isSingleDaySelected && (
                        <span className="block text-xs text-amber-500 font-semibold">Single Day Selected</span>
                      )}
                    </>
                  )}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-indigo-500/10 text-indigo-500 flex items-center justify-center">
                <FiCalendar className="h-5 w-5" />
              </div>
            </div>
          </div>

          {/* Report Entries List */}
          <div className="space-y-4">
            {visibleReports.map((report) => {
              const isExpanded = expandedReports.has(report._id);
              const masterName = report.masterId?.name || report.masterId?.email || 'Unknown Master';
              const masterEmail = report.masterId?.email || '';
              const followersList = report.usersId || [];
              const dateObj = new Date(report.createdAt);
              const formattedDate = dateObj.toLocaleDateString(undefined, {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              });
              const formattedTime = dateObj.toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit',
              });

              const isWeekend = isNonWorkingDay(report.createdAt);
              const isExcludedCard = effectiveExcludeWeekends && isWeekend;
              const cardDailyFee = isExcludedCard ? 0 : followersList.length * feeCalculation.rate;

              return (
                <div
                  key={report._id}
                  className={`bg-surface border rounded-2xl overflow-hidden shadow-sm transition-all ${isExcludedCard ? 'border-amber-500/30 opacity-75' : 'border-border'
                    }`}
                >
                  {/* Card Header */}
                  <div
                    onClick={() => toggleExpand(report._id)}
                    className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 cursor-pointer hover:bg-background/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                      <div className={`w-11 h-11 rounded-xl border flex items-center justify-center flex-shrink-0 font-bold ${isExcludedCard ? 'bg-amber-500/10 border-amber-500/30 text-amber-500' : 'bg-primary/10 border-primary/20 text-primary'
                        }`}>
                        <FiUserCheck className="h-6 w-6" />
                      </div>

                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-base font-bold text-foreground break-anywhere">{masterName}</h3>
                          {masterEmail && (
                            <span className="text-xs text-muted break-anywhere">({masterEmail})</span>
                          )}
                          {isWeekend ? (
                            isExcludedCard ? (
                              <span className="px-2.5 py-0.5 bg-amber-500/15 text-amber-600 dark:text-amber-400 font-extrabold text-[11px] rounded-full border border-amber-500/30">
                                Non-Working Day (Excluded)
                              </span>
                            ) : (
                              <span className="px-2.5 py-0.5 bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 font-bold text-[11px] rounded-full border border-indigo-500/30">
                                Weekend (Included)
                              </span>
                            )
                          ) : (
                            <span className="px-2 py-0.5 bg-slate-500/10 text-slate-400 font-semibold text-[11px] rounded-md border border-slate-500/20">
                              Weekday
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted flex items-center gap-2 mt-0.5">
                          <FiClock className="h-3.5 w-3.5 shrink-0" />
                          <span>Date: <strong>{formattedDate}</strong> at {formattedTime}</span>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 self-end sm:self-auto">
                      <div className="text-right flex flex-wrap items-center justify-end gap-2">
                        <span className="px-3 py-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-extrabold text-sm rounded-full border border-emerald-500/20 inline-flex items-center gap-1.5">
                          <FiUsers className="h-4 w-4" />
                          {followersList.length} Follower{followersList.length === 1 ? '' : 's'}
                        </span>

                        <span className={`px-3 py-1 font-black text-xs rounded-full border ${isExcludedCard
                            ? 'bg-amber-500/10 text-amber-500 border-amber-500/30 line-through'
                            : 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30'
                          }`}>
                          Fee: ${cardDailyFee.toFixed(2)}
                        </span>
                      </div>

                      <button
                        type="button"
                        className="p-2 text-muted hover:text-foreground rounded-lg transition-colors"
                      >
                        {isExpanded ? <FiChevronUp className="h-5 w-5" /> : <FiChevronDown className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Follower List */}
                  {isExpanded && (
                    <div className="border-t border-border bg-background/50 p-4 sm:p-5 space-y-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-muted">
                          Followers List on {formattedDate} ({followersList.length})
                        </h4>
                        {isSingleDaySelected && (
                          <span className="text-xs text-amber-500 font-semibold bg-amber-500/10 px-2.5 py-1 rounded-md border border-amber-500/20">
                            Single Day Audit — DB Repetition Check
                          </span>
                        )}
                      </div>

                      {followersList.length === 0 ? (
                        <p className="text-xs text-muted italic">No followers recorded for this master on this date.</p>
                      ) : (
                        <div className="table-scroll">
                          <table className="w-full min-w-[560px] text-left border-collapse text-xs">
                            <thead>
                              <tr className="border-b border-border text-muted uppercase font-semibold">
                                <th className="py-2.5 px-3">#</th>
                                <th className="py-2.5 px-3">Follower Name</th>
                                <th className="py-2.5 px-3">Follower Email</th>
                                <th className="py-2.5 px-3 text-center">DB Repetition Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border/60">
                              {followersList.map((user, idx) => {
                                const userId = user?._id || `user-${idx}`;
                                const repeatInfo = userRepeatAnalysis[userId];
                                const repeatCount = repeatInfo ? repeatInfo.count : 1;
                                const isRepeated = repeatCount > 1;

                                return (
                                  <tr key={`${report._id}-${userId}-${idx}`} className="hover:bg-surface/80">
                                    <td className="py-2.5 px-3 text-muted font-medium">{idx + 1}</td>
                                    <td className="py-2.5 px-3 font-bold text-foreground">
                                      {user?.name || 'N/A'}
                                    </td>
                                    <td className="py-2.5 px-3 text-muted">
                                      {user?.email || 'N/A'}
                                    </td>
                                    <td className="py-2.5 px-3 text-center">
                                      {/* {isRepeated ? (
                                        <span className="px-2.5 py-1 bg-amber-500/15 text-amber-600 dark:text-amber-400 font-bold rounded-md border border-amber-500/30 inline-flex items-center gap-1">
                                          <FiAlertCircle className="h-3.5 w-3.5" />
                                          Unique
                                        </span>
                                      ) : ( */}
                                      <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-medium rounded text-[11px] inline-flex items-center gap-1">
                                        <FiCheckCircle className="h-3 w-3" />
                                        Unique
                                      </span>
                                      {/* )} */}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination Controls Footer (admin only) */}
          {!isMasterMode && (
          <div className="bg-surface border border-border rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs text-muted font-medium">
                Showing{' '}
                <strong>
                  {pagination.total > 0 ? (page - 1) * limit + 1 : 0} -{' '}
                  {Math.min(page * limit, pagination.total)}
                </strong>{' '}
                of <strong>{pagination.total}</strong> report entries
              </span>

              {/* Items per page selector */}
              <div className="flex items-center gap-1.5 text-xs text-muted">
                <span>Per Page:</span>
                <select
                  value={limit}
                  onChange={(e) => {
                    const newLimit = Number(e.target.value);
                    setLimit(newLimit);
                    setPage(1);
                  }}
                  className="px-2 py-1 bg-background border border-border rounded-lg text-foreground font-semibold focus:outline-none"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            {/* Page Buttons */}
            <div className="flex flex-wrap items-center gap-1.5 self-end sm:self-auto">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || isLoading}
                className="p-2 bg-background border border-border rounded-xl text-foreground hover:bg-primary/10 hover:text-primary transition-colors disabled:opacity-40 disabled:hover:bg-background disabled:hover:text-foreground"
                title="Previous Page"
              >
                <FiChevronLeft className="h-4 w-4" />
              </button>

              {/* Page Number Chips */}
              {Array.from({ length: Math.min(5, pagination.pages || 1) }, (_, i) => {
                let pNum = i + 1;
                if (pagination.pages > 5 && page > 3) {
                  pNum = page - 2 + i;
                  if (pNum > pagination.pages) pNum = pagination.pages - (4 - i);
                }

                return (
                  <button
                    key={pNum}
                    type="button"
                    onClick={() => setPage(pNum)}
                    className={`px-3 py-1.5 text-xs font-bold rounded-xl border transition-all ${page === pNum
                      ? 'bg-primary text-white border-primary shadow-sm'
                      : 'bg-background text-foreground border-border hover:border-primary/50'
                      }`}
                  >
                    {pNum}
                  </button>
                );
              })}

              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pagination.pages || 1, p + 1))}
                disabled={page >= (pagination.pages || 1) || isLoading}
                className="p-2 bg-background border border-border rounded-xl text-foreground hover:bg-primary/10 hover:text-primary transition-colors disabled:opacity-40 disabled:hover:bg-background disabled:hover:text-foreground"
                title="Next Page"
              >
                <FiChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminMasterFollowingReport;
